import React from 'react';
import Link from 'next/link';
import styled, { css } from 'styled-components';
import styledContainerQuery from 'styled-container-query';
import tinycolor from 'tinycolor2';

import { DposLoyaltyDefault as DposLoyaltyIcon } from 'src/assets/dpos-loyalty-default';
import usePaths from 'src/hooks/use-paths';
import { TierEarningBasis } from 'src/components/loyalty/rewards/dpos-utils';
import type { DutchiePosLoyaltyConfig, RewardsListV2 } from 'src/gql/graphql';
import { LoyaltyPassFooter } from 'src/components/loyalty/loyalty-pass/footer';
import { FooterContainer } from 'src/components/loyalty/loyalty-pass/footer.styles';
import { VerifyPhoneToRedeemNotice, WidgetGroup } from 'src/components/loyalty/verify-phone-to-redeem-notice';
import { useLoyaltyRedemptionPhoneVerification } from 'src/components/modals/loyalty-redemption-phone-verification';
import { useLoyalty } from './use-loyalty';

type LoyaltyProps = {
  isWidgetLink?: boolean;
  locationMargin?: string;
  activeEnterpriseId?: string | undefined;
  loyaltyEnterpriseConfigData?: DutchiePosLoyaltyConfig;
  rewardsEnterpriseData?: RewardsListV2;
};

export const LoyaltyTiers = ({
  isWidgetLink = true,
  locationMargin = '',
  activeEnterpriseId = undefined,
  loyaltyEnterpriseConfigData = undefined,
  rewardsEnterpriseData = undefined,
}: LoyaltyProps): JSX.Element | null => {
  const verifyPhone = useLoyaltyRedemptionPhoneVerification(activeEnterpriseId);
  const {
    formatValueForDisplay,
    formatExpirationDate,
    rewardsProgramName,
    balanceDaysToExpiration,
    badgeColor,
    currentTierName,
    currentTierThreshold,
    currentBalance,
    currentYearTierBalance,
    targetBalance,
    nextTierName,
    progressPercentageToNextTier,
    isSingleTier,
    isPoints,
    isDiscountRedemption,
    formatValueForDaysForExpire,
    getExpiresVerb,
    getExpiryUnit,
    loyaltyWidgetEnabled,
    soonestExpiringBalance,
    soonestExpirationDate,
    redemptionRequiresPhoneVerification,
  } = useLoyalty(activeEnterpriseId, loyaltyEnterpriseConfigData, rewardsEnterpriseData);
  const { href: baseHref } = usePaths();

  if (!loyaltyWidgetEnabled) {
    return null;
  }

  const url = `${baseHref}/user/loyalty`;
  const balanceToNextTier = targetBalance - currentYearTierBalance;
  const showSoonestExpiry = soonestExpiringBalance != null && !!soonestExpirationDate;

  return (
    <WidgetGroup $locationMargin={locationMargin}>
      <Wrapper href={isWidgetLink ? url : null} as={isWidgetLink ? Link : 'div'} $locationMargin='0'>
        <DispoInfo>
          <Image>
            <DposLoyaltyIcon />
          </Image>

          <div>
            <Name data-testid='loyalty-program-name'>{rewardsProgramName}</Name>
            {badgeColor && currentTierName && (
              <Tier $color={badgeColor} data-testid='loyalty-tier-badge'>
                {currentTierName}
              </Tier>
            )}
          </div>
        </DispoInfo>
        <Meter
          role='meter'
          aria-valuemin={currentTierThreshold}
          aria-valuemax={targetBalance}
          aria-valuenow={currentYearTierBalance}
          aria-labelledby={`${rewardsProgramName} progress`}
          data-testid='loyalty-progress-meter'
        >
          <MeterLabel data-testid='loyalty-spent-label'>
            {formatValueForDisplay(currentYearTierBalance, '', isPoints)} {isPoints ? 'pts' : 'dollars'} spent
          </MeterLabel>
          {!isSingleTier && nextTierName && (
            <>
              <MeterProgress $width={progressPercentageToNextTier} data-testid='loyalty-progress-bar' />
              <MeterLabel data-testid='loyalty-next-tier-label'>
                {formatValueForDisplay(balanceToNextTier > 0 ? balanceToNextTier : 0, '', isPoints)}{' '}
                {isPoints ? 'pts' : ''} to {nextTierName}
              </MeterLabel>
            </>
          )}
        </Meter>

        <RewardInfo>
          <RewardAmount data-testid='loyalty-points-balance'>
            <strong>{formatValueForDisplay(currentBalance, TierEarningBasis.points, isDiscountRedemption)}</strong>{' '}
            rewards<RewardAmountLabel> available to redeem towards purchase</RewardAmountLabel>
          </RewardAmount>
          {showSoonestExpiry && (
            <RewardExpire>
              {formatValueForDisplay(
                soonestExpiringBalance,
                getExpiryUnit(soonestExpiringBalance),
                isDiscountRedemption
              )}{' '}
              {getExpiresVerb(soonestExpiringBalance)} {formatExpirationDate(soonestExpirationDate)}
            </RewardExpire>
          )}
          {!showSoonestExpiry && currentBalance > 0 && (
            <RewardExpire>{formatValueForDaysForExpire(balanceDaysToExpiration ?? 0)}</RewardExpire>
          )}
        </RewardInfo>

        <LoyaltyPassFooter
          borderOverrides={css`
            left: -20px;
            right: -20px;
          `}
        />
      </Wrapper>
      {redemptionRequiresPhoneVerification && <VerifyPhoneToRedeemNotice onVerify={() => void verifyPhone()} />}
    </WidgetGroup>
  );
};

const DispoInfo = styled.div`
  align-items: center;
  display: flex;
  gap: 16px;
`;

const Image = styled.div`
  border-radius: 50%;
  color: var(--dct--customized-colors-buttonsLinks);
  display: block;
  height: 56px;
  overflow: hidden;
  width: 56px;
`;

const Name = styled.div`
  font-size: 16px;
  font-weight: 700;
  line-height: ${18 / 16};
`;

const Tier = styled.div<{ $color: string }>`
  background: ${({ $color }) => $color};
  border-radius: var(--dct--customized-radius-tags);
  color: ${({ $color }) => (tinycolor($color).isDark() ? '#ffffff' : '#121516')};
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  margin-top: 6px;
  padding: 4px 6px;
  text-transform: uppercase;
`;

const Meter = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: space-between;
  margin: 32px 0 20px;
`;

const MeterLabel = styled.div`
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
`;

const MeterProgress = styled.div<{ $width: string }>`
  background: linear-gradient(
    90deg,
    ${({ theme }) => tinycolor.mix(theme.customized.colors.buttonsLinks, '#000000', 50).toString()} 0%,
    ${({ theme }) => tinycolor.mix(theme.customized.colors.buttonsLinks, '#000000', 50).toString()}
      ${({ $width }) => $width},
    #fff ${({ $width }) => $width}
  );
  border-radius: var(--dct--customized-radius-sm);
  height: 8px;
  order: -1;
  position: relative;
  width: 100%;
`;

const RewardInfo = styled.div`
  align-items: center;
  background: ${({ theme }) => tinycolor.mix(theme.customized.colors.buttonsLinks, '#000000', 50).toString()};
  border-radius: var(--dct--customized-radius-md);
  display: flex;
  gap: 12px;
  justify-content: space-between;
  line-height: 1;
  padding: 16px 12px;
`;

const RewardAmount = styled.div`
  font-size: 14px;
  font-weight: 400;
  strong {
    font-weight: 600;
  }
`;

const RewardAmountLabel = styled.span`
  display: none;
`;

const RewardExpire = styled.div`
  font-size: 12px;
`;

const Wrapper = styledContainerQuery.a`
  background: var(--dct--customized-colors-buttonsLinks);
  border-radius: var(--dct--customized-radius-lg);
  color: #fff;
  display: block;
  margin: ${({ $locationMargin }) => $locationMargin || '20px auto 40px'};
  max-width: 1200px;
  overflow: hidden;
  padding: 20px;
  position: relative;
  width: 100%;
  z-index: 1;

  /* In block mode, add margin-top to footer for spacing */
  ${FooterContainer} {
    margin-top: 20px;
  }

  &::before {
    background: #ffffff1a;
    border-radius: 50%;
    content: '';
    height: 400px;
    left: 48px;
    pointer-events: none;
    position: absolute;
    top: 48px;
    transform: translate(-50%, -50%);
    width: 400px;
    z-index: -1;
  }

  &:container(min-width: 560px) {
    ${RewardAmountLabel} {
      display: inline;
    }
  }

  &:container(min-width: 600px) {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 32px;
    justify-content: space-between;

    /* In flex mode, remove margin-top from footer since gap provides spacing */
    ${FooterContainer} {
      margin-top: 0;
    }

    ${Meter} {
      align-items: center;
      gap: 16px;
      margin: 0;
      order: 3;
      width: 100%;
    }

    ${MeterProgress} {
      flex: 1;
      order: unset;
      width: auto;
    }

    ${RewardInfo} {
      margin-top: -12px;
      order: 4;
      width: 100%;
    }
  }

  &:container(min-width: 900px) {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 32px;
    justify-content: space-between;

    ${DispoInfo} {
      max-width: 218px;
    }

    ${MeterProgress} {
      flex: 1;
      order: unset;
      width: auto;
    }

    ${Meter} {
      align-items: center;
      gap: 16px;
      margin: 0;
      order: 3;
      width: 100%;
    }
  }

  &:container(min-width: 1080px) {
    gap: 32px;
    justify-content: flex-start;

    ${DispoInfo} {
      max-width: 218px;
    }

    ${Meter} {
      flex: 1;
      order: unset;
      width: auto;
    }

    /* At full homepage width the reward box sits inline as the third column */
    ${RewardInfo} {
      flex: none;
      margin-top: 0;
      order: unset;
      width: 264px;
    }
    ${RewardAmountLabel} {
      display: none;
    }
  }
`;
